import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { extendObjectWithId } from 'src/shared/functions/extendObjectWithId';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import { GridCellParams, GridColDef, GridRowParams } from '@mui/x-data-grid';
import { Box, Link, Tooltip } from '@mui/material';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { getAllSwishConsents } from 'src/shared/services/swishConsentService';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { SwishConsent } from '../models/swishConsent';
import { SwishConsentSearchFilter } from '../models/swishConsentSearchFilter';
import { SwishConsentModal } from './swishConsentModal';
import { SwishConsentStatus } from './swishConsentStatus';

type SwishConsentDataGridProps = {
  searchFilter: SwishConsentSearchFilter;
  searchFilterCallback: (searchFilter: SwishConsentSearchFilter) => void;
};

// Sorterbara kolumnfält. En sortCol utanför denna lista (t.ex. ett gammalt sparat värde i localStorage)
// får MUI DataGrid att återställa sorteringen i en oändlig onSortModelChange-loop — fall därför tillbaka
// till createdAt (senast skapade överst).
const SORTABLE_FIELDS = ['payerAlias', 'status', 'activatedAt', 'createdAt'];
const DEFAULT_SORT_FIELD = 'createdAt';

export const SwishConsentDataGrid = (props: SwishConsentDataGridProps) => {
  const { searchFilter, searchFilterCallback } = props;
  const { t } = useTranslation(['invoice', 'common']);
  const isMounted = useIsMounted();

  const { scrollIntoView, scrollRef } = useScrollIntoView();

  const [rows, setRows] = useState<SwishConsent[]>([]);
  const [selectedConsentId, setSelectedConsentId] = useState<number | null>(null);

  const [data, setData] = useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: searchFilter.pageSize,
    page: searchFilter.pageIndex,
  });

  const updateData = <K extends keyof DataGridModel>(k: K, v: DataGridModel[K]) => setData((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    setData((prev) => ({ ...prev, ...{ page: searchFilter.pageIndex, pageSize: searchFilter.pageSize } }));
  }, [searchFilter]);

  const onPageChange = (newPageIndex: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: newPageIndex });
    scrollIntoView();
  };

  const onPageSizeChange = (newPageSize: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: 0, pageSize: newPageSize });
    scrollIntoView();
  };

  const fetchData = useCallback(async () => {
    if (isMounted()) {
      updateData('loading', true);
    }
    try {
      const result = await getAllSwishConsents(searchFilter);
      const consentResult = extendObjectWithId(result.data.results, 'swishConsent_id');
      updateData('totalRows', result.data.totalItems);

      setTimeout(() => {
        setRows(consentResult);
      }, 100);
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        updateData('loading', false);
      }
    }
  }, [isMounted, searchFilter]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [fetchData, isMounted]);

  const onRowClick = (params: GridRowParams) => {
    setSelectedConsentId(params.row.swishConsent_id as number);
  };

  // Säkra att sortModel alltid pekar på ett giltigt fält (annars MUI-loop vid gammalt/ogiltigt sparat värde).
  const effectiveSortCol = SORTABLE_FIELDS.includes(searchFilter.sortCol) ? searchFilter.sortCol : DEFAULT_SORT_FIELD;

  // Kopplad till: klickbar länk till medlem (reg_member) eller givare (fnd_donor). Speglar autogiro-griden.
  const linkedName = (row: SwishConsent) => {
    if (row.consentType === 'donor') return row.donorName ?? '';
    return `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim();
  };

  const columns: GridColDef[] = [
    {
      field: 'payerAlias',
      headerName: t('invoice:swishStatus.consents.tableHeader.payerAlias'),
      sortable: true,
      flex: 0.9,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.payerAlias}</>;
      },
    },
    {
      field: 'status',
      headerName: t('invoice:swishStatus.consents.tableHeader.status'),
      sortable: true,
      flex: 0.9,
      renderCell: (params: GridCellParams) => {
        return <SwishConsentStatus item={params.row} />;
      },
    },
    {
      field: 'activatedAt',
      headerName: t('invoice:swishStatus.consents.tableHeader.activatedAt'),
      sortable: true,
      flex: 0.9,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.activatedAt ? DateHelper.getDateTime(params.row.activatedAt) : ''}</>;
      },
    },
    {
      field: 'createdAt',
      headerName: t('invoice:swishStatus.consents.tableHeader.createdAt'),
      sortable: true,
      flex: 0.9,
      renderCell: (params: GridCellParams) => {
        return <>{DateHelper.getDateTime(params.row.createdAt)}</>;
      },
    },
    {
      field: 'linkedTo',
      headerName: t('invoice:swishStatus.consents.tableHeader.linkedTo'),
      sortable: false,
      flex: 1.5,
      renderCell: (params: GridCellParams) => {
        const row = params.row as SwishConsent;
        const isDonor = row.consentType === 'donor';
        const hasMember = !!row.customer_id;
        const hasDonor = !!row.donor_id;
        if (!hasMember && !hasDonor) return <>–</>;
        const name = linkedName(row) || '–';
        const href = isDonor ? `${RoutesEnum.DonorsDetail}/${row.donor_id}` : `${RoutesEnum.MemberDetails}/${row.customer_id}`;
        const tooltip = isDonor ? t('invoice:swishStatus.consents.tableHeader.donor') : t('invoice:swishStatus.consents.tableHeader.member');
        return (
          <Tooltip title={tooltip}>
            <Link href={href} onClick={(e) => e.stopPropagation()} underline='always'>
              {name}
            </Link>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        '& .MuiPaper-root': {
          boxShadow: 'none',
        },
      }}
      ref={scrollRef}
    >
      <DataGrid<SwishConsent>
        autoHeight
        columns={columns}
        rows={rows}
        disableSelectionOnClick
        onRowClick={onRowClick}
        dataModel={data}
        checkboxSelection={false}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        selectionModel={[]}
        sortModel={[
          {
            field: effectiveSortCol,
            sort: searchFilter.sortDir,
          },
        ]}
        onSortModelChange={(newSortModel) => {
          if (newSortModel.length > 0) {
            const { field } = newSortModel[0];
            searchFilterCallback({
              ...searchFilter,
              pageIndex: 0,
              sortCol: field,
              sortDir: newSortModel[0].sort,
            });
          } else {
            searchFilterCallback({
              ...searchFilter,
              pageIndex: 0,
              sortCol: searchFilter.sortCol,
              sortDir: searchFilter.sortDir === 'asc' ? 'desc' : 'asc',
            });
          }
        }}
        onSelectionModelChange={() => ConsoleHelper.log('')}
      />
      {selectedConsentId !== null && (
        <SwishConsentModal
          modalOpen
          swishConsentId={selectedConsentId}
          onClose={() => setSelectedConsentId(null)}
          onSave={() => {
            setSelectedConsentId(null);
            fetchData();
          }}
        />
      )}
    </Box>
  );
};
